import { useState } from "react";
import { Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { GovItemDetail, approveSignOff, rejectSignOff } from "../../../../utils/icGovernance";
import { SignOffChip, shortDate } from "../governanceStatus";
import ActionError from "./ActionError";
import SideCard from "./SideCard";
import { ItemActionProps } from "./types";
import { FAINT, MUTED, approveButtonSx, quoteSx, rejectButtonSx } from "./ui";
import { useGovAction } from "./useGovAction";

type Choice = "approve" | "reject";

/**
 * Sign-off card (shown when `signOffEnabled`). Approve / Reject only when
 * `canDecideSignOff`; Reject needs a comment.
 */
const SignOffCard = ({ item, onUpdated, onReload }: { item: GovItemDetail } & ItemActionProps) => {
  const { signOff } = item;
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState<Choice | null>(null);
  const action = useGovAction();
  const decided = signOff.status === "APPROVED" || signOff.status === "REJECTED";
  const requested = !!(signOff.requestedBy || signOff.requestedAt);

  const decide = async (choice: Choice) => {
    const text = comment.trim();
    if (choice === "reject" && !text) return;
    setPending(choice);
    const detail = await action.run(() =>
      choice === "approve" ? approveSignOff(item.id, text || undefined) : rejectSignOff(item.id, text),
    );
    setPending(null);
    if (detail) {
      setComment("");
      onUpdated(detail, { text: choice === "approve" ? "Sign-off approved" : "Sign-off rejected" });
    }
  };

  return (
    <SideCard title="Sign-off" action={<SignOffChip status={signOff.status} />}>
      {signOff.status === "NONE" && !requested ? (
        <Typography variant="body2" sx={{ color: FAINT }}>
          Sign-off has not been requested yet.
        </Typography>
      ) : null}

      {requested ? (
        <Box sx={{ mb: decided ? 1.5 : 0 }}>
          <Typography variant="body2" sx={{ color: MUTED }}>
            Requested by <b>{signOff.requestedBy || "—"}</b> · {shortDate(signOff.requestedAt)}
          </Typography>
          {signOff.requestComment ? (
            <Typography variant="body2" sx={quoteSx}>
              {signOff.requestComment}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {decided ? (
        <Box>
          <Typography variant="body2" sx={{ color: MUTED }}>
            {signOff.status === "APPROVED" ? "Approved" : "Rejected"} by <b>{signOff.decidedBy || "—"}</b> ·{" "}
            {shortDate(signOff.decidedAt)}
          </Typography>
          {signOff.comment ? (
            <Typography variant="body2" sx={quoteSx}>
              {signOff.comment}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {item.canDecideSignOff ? (
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Comment (required to reject)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={action.busy}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              disableElevation
              onClick={() => decide("approve")}
              disabled={action.busy}
              startIcon={pending === "approve" ? <CircularProgress size={16} color="inherit" /> : null}
              sx={approveButtonSx}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => decide("reject")}
              disabled={action.busy || !comment.trim()}
              startIcon={pending === "reject" ? <CircularProgress size={16} color="inherit" /> : null}
              sx={rejectButtonSx}
            >
              Reject
            </Button>
          </Stack>
          <ActionError action={action} onReload={onReload} />
        </Box>
      ) : null}
    </SideCard>
  );
};

export default SignOffCard;
